import { Application } from 'pixi.js'
import { logger } from './framework/core/Logger'
import { EventBus } from './framework/events/EventBus'
import { orientationOf } from './framework/render/design'
import { StageScaler } from './framework/render/StageScaler'
import { detectLocale, loadLocaleFont, setLocale } from './framework/i18n/i18n'
import type { Orientation, Resizable } from './framework/render/design'
import { GameController } from './game/GameController'
import { MockSpinService } from './game/net/MockSpinService'
import { RemoteSpinService } from './game/net/RemoteSpinService'
import { GameServerClient } from './framework/net/GameServerClient'
import { isServerError } from './framework/net/ServerError'
import { toClientBalance } from './framework/net/balance'
import { setCurrentCurrency } from './framework/core/currency'
import type { SpinService } from './game/net/SpinService'
import { ReelsView } from './game/view/ReelsView'
import { BottomBarView } from './game/view/BottomBarView'
import { BackgroundView } from './game/view/BackgroundView'
import { LogoView } from './game/view/hud/LogoView'
import { CharacterView } from './game/view/hud/CharacterView'
import { WaysView } from './game/view/hud/WaysView'
import { MultiplierView } from './game/view/hud/MultiplierView'
import { BuyBonusButton } from './game/view/hud/BuyBonusButton'
import { BuyBonusDialog } from './game/view/hud/BuyBonusDialog'
import { BetListPopup } from './game/view/hud/BetListPopup'
import { AutoSpinPanel } from './game/view/hud/AutoSpinPanel'
import { MenuPopup } from './game/view/hud/MenuPopup'
import { InfoPanel } from './game/view/hud/InfoPanel'
import { SystemDialog } from './game/view/hud/SystemDialog'
import { registerSystemDialog, showError, showErrorCode } from './game/view/hud/dialogMediator'
import { CollectorView } from './game/view/hud/CollectorView'
import { WildInfoView } from './game/view/hud/WildInfoView'
import { StatusBarView } from './game/view/hud/StatusBarView'
import { RewardOverlayView } from './game/view/overlay/RewardOverlayView'
import { FxLayerView } from './game/view/FxLayerView'
import { OpeningCurtainView } from './game/view/OpeningCurtainView'
import { LoadingView } from './game/view/LoadingView'
import { loadLoadingAssets } from './game/assets/LoadingAssets'
import { setLoadingProgress, markGameReady } from './boot/loadingProgress'
import { AudioManager } from './framework/audio/AudioManager'
import { loadSymbolTextures } from './game/assets/SymbolTextures'
import { loadSymbolSpines } from './game/assets/SymbolSpine'
import { loadCharacterSpine } from './game/assets/CharacterSpine'
import { loadRewardSpines } from './game/assets/RewardSpine'
import { loadDeferredEffectSpines, loadEffectSpines } from './game/assets/EffectSpines'
import { markSpineBootComplete, setSpineBundleProgressListener } from './framework/assets/spineBundle'
import { loadBackgroundTextures } from './game/assets/BackgroundTextures'
import { loadUiTextures } from './game/assets/UiTextures'
import { loadNumberFont } from './game/assets/NumberFont'
import type { GameEventMap } from './game/events/GameEvents'

interface SpinSession {
  readonly service: SpinService
  readonly balance?: number
  readonly betOptions?: readonly number[]
  /** 已連線的後端客戶端（Mock 模式為 undefined）；供橋接 Kicked/斷線通知。 */
  readonly client?: GameServerClient
  /** 有設定後端位址但連線／登入失敗（已回退 Mock）；正式版需照 Unity 提示錯誤對話框。 */
  readonly loginFailed?: boolean
  /**
   * 帶 token 登入被後端拒（Login 回 code!=0）時的具體錯誤碼；正式版照 Unity OnLogin 顯示
   * Error_Code_{code}（比通用 Error_Login 精確）。連不上／握手失敗等非 ServerError 情況為 undefined。
   */
  readonly loginErrorCode?: number
}

// 依 VITE_SERVER_URL 決定連真實後端（RemoteSpinService）或離線 Mock；連線／登入失敗自動回退 Mock。
async function createSpinService(): Promise<SpinSession> {
  const url = import.meta.env.VITE_SERVER_URL
  if (typeof url === 'string' && url.length > 0) {
    try {
      const gameName = import.meta.env.VITE_GAME_NAME ?? 'Slot026'
      const gameCmd = import.meta.env.VITE_GAME_CMD ?? gameName
      const currency = import.meta.env.VITE_CURRENCY ?? 'PHP'
      const client = new GameServerClient()
      await client.connect(url, { gameName, currency })
      const betOptions = client.betRange.map((value) => toClientBalance(value))
      logger.info(`已連線後端 ${url}，使用 RemoteSpinService（gameCmd=${gameCmd}）`)
      return {
        service: new RemoteSpinService(client, gameCmd),
        balance: toClientBalance(client.balance),
        client,
        ...(betOptions.length > 0 ? { betOptions } : {}),
      }
    } catch (error) {
      logger.error('連線後端失敗，改用離線 MockSpinService', error)
      // 帶 token 但後端拒登入（ServerError）→ 帶出具體 code，正式版照 Unity 顯示 Error_Code_{code}。
      if (isServerError(error)) {
        return { service: new MockSpinService(), loginFailed: true, loginErrorCode: error.code }
      }
      return { service: new MockSpinService(), loginFailed: true }
    }
  }
  return { service: new MockSpinService() }
}

// Composition root — 取代 Unity GameLifetimeScope（VContainer）。
async function bootstrap(): Promise<void> {
  const mount = document.getElementById('app')
  if (mount === null) {
    throw new Error('找不到掛載節點 #app')
  }

  // 依網址 ?l=／?lang= 設定語系（對齊 Unity：無參數即預設 en、不讀瀏覽器語言）；
  // 早於任何 view 建立，讓 UI 以正確語系/字型呈現。
  setLocale(detectLocale())

  const app = new Application()
  await app.init({
    background: '#0b0b14',
    resizeTo: window,
    antialias: true,
    autoDensity: true,
    resolution: window.devicePixelRatio || 1,
  })
  mount.appendChild(app.canvas)

  // ── Loading #1（combo 平台開場，index.html 的 DOM 疊層）進度：PixiJS 就緒 → 載入遊戲 loading 版面美術 ──
  // 此階段畫面仍是 combo 疊層（蓋在 canvas 上），進度餵給 window.__GAME_LOADING。
  setLoadingProgress(0.15)
  await loadLoadingAssets()
  setLoadingProgress(0.5)

  // ── Loading #2（遊戲 loading，PixiJS）建立並顯示；combo 疊層補滿淡出、無縫接棒 ──
  const loading = new LoadingView()
  app.stage.addChild(loading)
  const layoutLoading = (): void =>
    loading.resize(app.screen.width, app.screen.height, orientationOf(app.screen.width, app.screen.height))
  layoutLoading()
  // ⚠️ 訂閱 renderer 的 'resize'（尺寸套用「之後」才觸發），不可用 window 'resize'：
  //    PixiJS resizeTo:window 以 RAF 延後套用，在 window 'resize' 當下讀 app.screen 會拿到舊尺寸與
  //    舊 orientation → 直版視窗仍套橫版版面（畫面只剩放大的角色、其餘元件跑到視窗外）。
  //    同 StageScaler 的作法（見 framework/render/StageScaler.ts 檔頭）。
  app.renderer.on('resize', layoutLoading)
  loading.setProgress(0)
  markGameReady() // 遊戲 loading 版面已就緒 → 通知 combo 疊層補滿並淡出

  // 其餘遊戲資源（貼圖／Spine／音效／字型）在遊戲 loading 畫面下載入；每完成一項推進進度條（後半段）。
  // ⚠️ 報獎（ani_win/cutscene/maxWin）與免費遊戲專用特效（trans/freespins/fly/reelGlow）合計約 17MB
  //    **刻意不在這裡**：見下方 loading 淡出後的背景載入。呼叫端（RewardOverlayView／FxLayerView）
  //    播放前會自行 await，因此即使背景載入還沒跑完也不會播到缺資產的字卡。
  // ⚠️ 進度**不可**用「完成的 loader 數 / 總數」平均推進：各項位元組量差了兩個數量級
  //    （spine 打包檔 core 分片約 34MB、貼圖合圖約 3.8MB、字型子集 0.14MB），而且三個 spine loader
  //    共用同一批分片下載、必然一起卡在最後 → 進度條會整段停住不動。
  //    實測（production build／Fast 4G／冷快取）：停 88% 達 19s、再停 94% 達 2.2s 才跳 100%。
  //    改為位元組加權：spine 由 spineBundle 的串流下載＋頁圖解碼即時回報（見該檔 reportProgress），
  //    其餘小項仍按完成數計。
  const SPINE_WEIGHT = 0.8
  const spineJobs = [loadSymbolSpines(), loadCharacterSpine(), loadEffectSpines()]
  const otherJobs = [
    loadSymbolTextures(),
    loadBackgroundTextures(),
    loadUiTextures(),
    loadNumberFont(),
    loadLocaleFont(), // 當前語系的 Unity 原生字型（Font_AssetTable 對應 ttf；失敗回退系統字型）
  ]
  let spineFraction = 0
  let spineLoadersDone = 0
  let otherLoadersDone = 0
  let lastPushed = -1

  const pushProgress = (): void => {
    // 取 max：bundle 不可用（index.json 取不到、退回散檔）時 spineFraction 恆為 0，
    // 此時改由已完成的 spine loader 數推進，行為退化成原本的粗略分段但不會卡在 0。
    const spine = Math.max(spineFraction, spineLoadersDone / spineJobs.length)
    const other = otherLoadersDone / otherJobs.length
    const value = SPINE_WEIGHT * spine + (1 - SPINE_WEIGHT) * other
    // 串流回報頻繁（每個 read chunk 一次），做最小變化量節流以免每幀重啟補間。
    if (value < lastPushed + 0.004 && value < 1) return
    lastPushed = value
    loading.setProgress(value)
  }

  // 於 loader 啟動後才註冊：漏掉的前幾個 chunk 無妨（reportProgress 回報的是累計比例，非增量）。
  setSpineBundleProgressListener((fraction) => {
    spineFraction = fraction
    pushProgress()
  })

  await Promise.all([
    ...spineJobs.map((job) =>
      job.then(() => {
        spineLoadersDone += 1
        pushProgress()
      }),
    ),
    ...otherJobs.map((job) =>
      job.then(() => {
        otherLoadersDone += 1
        pushProgress()
      }),
    ),
  ])
  setSpineBundleProgressListener(null) // 之後的 deferred 分片是背景載入，與 loading 進度無關

  // ── 連線後端並定案當前幣別（必須早於任何 view 建立）──
  // Unity 是收到登入回應後才 SetCurrencyUI；本移植同理：幣別符號與餘額金額色在各 view 建構時就要
  // 正確（BottomBarView 的 numText 色、winCurrency 文字、BuyBonusDialog 的 MoneyRow 色等皆於建構子
  // 讀取當前幣別），故 createSpinService（含連線／握手／登入）與 setCurrentCurrency 排在 view 之前。
  const session = await createSpinService()
  // 後端主動下推的中止事件 → 共用訊息窗（對照 Unity GameServerHandler：一律 ShowError/ShowErrorCode，
  // 玩家按確認後發 ErrorLogEvent 回大廳／重整頁面）。dialogMediator 在窗註冊前會排隊，故此處先掛無妨。
  session.client?.on('kicked', ({ code }) => showErrorCode(code))
  session.client?.on('reconnectFailed', () => showError('dialog.Error_Network'))
  session.client?.on('packetError', () => showError('dialog.Error_Disconnect'))
  // 後端幣別代碼（ResponseInfo.currency）→ 當前幣別；離線 Mock 退回 .env 的 VITE_CURRENCY（預設 PHP）。
  const currencyCode =
    session.client !== undefined && session.client.currency.length > 0
      ? session.client.currency
      : (import.meta.env.VITE_CURRENCY ?? 'PHP')
  setCurrentCurrency(currencyCode)

  // ── 依賴組裝（Mock 可玩流程：見 docs/PIXI_MIGRATION_PLAN.md 階段 2-4） ──
  const bus = new EventBus<GameEventMap>()
  const background = new BackgroundView()
  background.initSpine() // ani_bg 動態背景（loadEffectSpines 已完成；失敗時內部自動 fallback 靜態圖）
  const reels = new ReelsView(bus)
  const multiplier = new MultiplierView()
  const collector = new CollectorView()
  const wildInfo = new WildInfoView(bus)
  const character = new CharacterView(bus)
  const ways = new WaysView(bus)
  const logo = new LogoView()
  const buyBonus = new BuyBonusButton(bus)
  const bottomBar = new BottomBarView(bus)
  const statusBar = new StatusBarView(bus)
  const buyBonusDialog = new BuyBonusDialog(bus)
  const betListPopup = new BetListPopup(bus, () => bottomBar.betButtonAnchor())
  const autoSpinPanel = new AutoSpinPanel(bus)
  // 設定選單是掛在設定鈕上方的就地浮層（非彈窗），故需底部列提供該鈕的當前位置。
  const menuPopup = new MenuPopup(bus, () => bottomBar.menuButtonAnchor())
  const infoPanel = new InfoPanel(bus)
  // 全遊戲唯一的共用訊息窗（對照 Unity DialogMediator 持有唯一 Dialog 實例）。
  const systemDialog = new SystemDialog()
  registerSystemDialog(systemDialog)
  const rewardOverlay = new RewardOverlayView(bus)
  const fx = new FxLayerView()
  // 開幕紅幕（Continue 之後蓋住畫面再拉開）。此時 core spine 已載完，故建構子就能建骨架；
  // 為了蓋在 loading 之上，addChild 延後到 Continue 點擊後（見下方）。
  const openingCurtain = new OpeningCurtainView()
  // 資訊頁「一般條款」的兩個動態值（對照 Unity InfoPanel.SetLocalize）：幣別**代碼**（currencyCode，
  // 已於上方連線後定案）與 ClientOptions.RTP[0]。離線 Mock 無後端資料 → RTP 交由 commonSections 標示為示意值。
  infoPanel.setServerInfo(currencyCode, session.client?.rtp[0] ?? '')

  const controller = new GameController({
    bus,
    spinService: session.service,
    reels,
    background,
    multiplier,
    collector,
    character,
    overlay: rewardOverlay,
    fx,
    spinTarget: bottomBar,
    ...(session.balance !== undefined ? { initialBalance: session.balance } : {}),
    ...(session.betOptions !== undefined ? { betOptions: session.betOptions } : {}),
  })
  new AudioManager(bus)

  // z-order（底→頂）＝ Unity LandscapeUI.prefab 的 Canvas／兄弟節點順序，逐項對照：
  //   GameCanvas      MGBackground・FGBackground → Character → Board → WildInfoGroup → WaysInfo
  //                   → FreeSpinIngo（收集面板）→ MultiplierInfo（ReelGlow 併入 fx 層）
  //   GameFrontCanvas Logo
  //   UICanvas        BuyTypeButtons → BottomBar → OtherInfo
  //   UIFrontCanvas   ScoreEffectGroup・BulletPool ┐
  //   TopCanvas       TransSpine・PreWin           ┘＝ fx 特效層
  // ⚠️ 角色立繪的 z-order 依方向而異（見下方 characterZOrder）：**橫版疊在盤面之上**（美術取向，
  //    使用者指定，刻意偏離 Unity 的 Character→Board 順序）、**直版排在盤面之後**＝被盤面木框遮住
  //    （同 Unity GameCanvas 的 Character→Board 順序；直版重疊面積大，露出會蓋掉盤面）。此處初始
  //    先排在 reels 之前（直版行為），啟動時 scaler.layout() 會依實際方向即時修正。
  // menuPopup／betListPopup 是掛在底部列鈕上方的**非彈窗**浮層（無暗底、不攔截全螢幕），故緊接在底部列之上。
  // 其餘操作彈窗與 FG 進場/結算字卡皆為全螢幕暗背景覆蓋層（攔截點擊），Unity 亦另開 canvas，疊在最上層。
  app.stage.addChild(
    background,
    character,
    reels,
    wildInfo,
    ways,
    collector,
    multiplier,
    logo,
    buyBonus,
    bottomBar,
    statusBar,
    menuPopup,
    betListPopup,
    fx,
    buyBonusDialog,
    autoSpinPanel,
    infoPanel,
    rewardOverlay,
    systemDialog,
  )

  // 角色立繪 z-order 依方向切換（見上方 addChild 註解）：橫版疊在盤面「之上」、直版排在盤面「之後」。
  // z-order 只與方向有關，故僅在方向改變時重排 stage children（避免每次 resize 都搬動節點）。
  let characterOrientation: Orientation | null = null
  const characterZOrder: Resizable = {
    resize: (_width, _height, orientation): void => {
      if (orientation === characterOrientation) return
      characterOrientation = orientation
      if (character.parent !== app.stage || reels.parent !== app.stage) return
      // 先抽出 character，再讀 reels 當前索引後插回其前／後——此刻陣列無 character，索引語意零歧義。
      app.stage.removeChild(character)
      const reelsIndex = app.stage.getChildIndex(reels)
      app.stage.addChildAt(character, orientation === 'landscape' ? reelsIndex + 1 : reelsIndex)
    },
  }

  const resizables: Resizable[] = [
    background,
    reels,
    multiplier,
    collector,
    wildInfo,
    character,
    ways,
    logo,
    buyBonus,
    bottomBar,
    statusBar,
    menuPopup,
    fx,
    buyBonusDialog,
    betListPopup,
    autoSpinPanel,
    infoPanel,
    rewardOverlay,
    systemDialog,
    openingCurtain,
    characterZOrder,
  ]
  const scaler = new StageScaler(app, resizables)
  scaler.layout()

  controller.showIdleBoard()

  // ── 載入完成：遊戲 loading 顯示 Continue 按鈕，等玩家點擊後淡出、露出遊戲畫面 ──
  // ⚠️ resize 監聽要留到玩家點完 Continue 才解除：等待期間若轉向／改視窗大小，
  //    先解除會讓 loading 停在舊尺寸的版面（畫面殘留放大的角色、進度條跑到框外）。
  app.stage.addChild(loading) // 置於最上層（蓋住已組裝好的遊戲畫面）
  await loading.awaitContinue()
  app.renderer.off('resize', layoutLoading)
  // Continue 點擊是使用者手勢：於此解鎖 AudioContext。
  // 主 BGM 不在此啟動——Unity 是開幕紅幕播完才 mu_main_background（見 openingCurtainFinished）。
  bus.emit('audioUnlockRequested', undefined)

  // ── 開幕紅幕（Unity SlotStateMachine.OnUILoadingContinueClickEvent → GamePlayUI.PlayOpening）──
  // Unity 時序：同一事件的 High 優先訂閱者 LoadingUIMediator 立刻 Destroy loading UI，Low 優先才播布幕，
  // 而布幕第一格就是合攏狀態 → 觀感上「loading 消失、紅幕接手蓋住畫面」，中間不露出遊戲畫面。
  // 故此處先 showClosed() 蓋住（布幕已 addChild 在 loading 之上），才移除 loading。
  // 骨骼不可用時退回既有的 loading 淡出；se_open 與 BGM 時序不受影響（同 FxLayerView 的 fallback 慣例）。
  app.stage.addChild(openingCurtain)
  if (openingCurtain.showClosed()) {
    loading.destroy({ children: true })
  } else {
    await loading.fadeOutAndDestroy()
  }
  bus.emit('openingCurtainStarted', undefined)
  await openingCurtain.playOpen()
  bus.emit('openingCurtainFinished', undefined)
  openingCurtain.destroy({ children: true })

  // ── 非首屏資產（約 17MB）在遊戲已可操作後才背景載入 ──
  // 報獎字卡與 FG 專用特效在「按下第一次 SPIN」之前絕不會出現，先讓玩家進得去比較重要。
  // 兩者的呼叫端播放前都會 await 同一份任務，所以就算玩家立刻中大獎也只是多等下載、不會缺圖。
  // ⚠️ 這批分組必須與 scripts/pack-spine-bundle.mjs 的 DEFERRED_DIRS 一致（不一致時 DEV 會印警告）。
  markSpineBootComplete()
  void Promise.all([loadRewardSpines(), loadDeferredEffectSpines()]).then(
    () => logger.info('非首屏 spine 資產背景載入完成'),
    (error: unknown) => logger.warn('非首屏 spine 資產背景載入失敗（播放時會再重試）', error),
  )

  // 有設定後端卻連不上／登入失敗：正式版照 Unity BaseGameService 提示錯誤對話框（確認後重整），
  // 不能讓玩家在不知情下玩到離線 Mock 的假餘額。開發模式維持既有的自動回退 Mock（後端沒開也能開發），
  // 僅留 log。此提示等到 loading 淡出後才彈，避免被 loading 疊層蓋住。
  // 帶 token 被後端拒登入 → Error_Code_{code}（對齊 Unity OnLogin）；連不上／握手失敗等 → 通用 Error_Login。
  if (session.loginFailed === true && !import.meta.env.DEV) {
    if (session.loginErrorCode !== undefined) showErrorCode(session.loginErrorCode)
    else showError('dialog.Error_Login')
  }

  // 開發用：主畫面右上角「重載 Loading」浮動按鈕，一鍵重新載入以重播 combo → 遊戲 loading 兩段流程（僅 dev 模式）。
  if (import.meta.env.DEV && document.getElementById('dev-reload-loading') === null) {
    const reloadBtn = document.createElement('button')
    reloadBtn.id = 'dev-reload-loading'
    reloadBtn.textContent = '↻ Loading'
    reloadBtn.title = '開發用：重新載入以重播 combo → 遊戲 loading'
    reloadBtn.style.cssText =
      'position:fixed;top:10px;right:10px;z-index:10000;padding:6px 12px;' +
      'font:bold 13px monospace;color:#ffcf5a;background:rgba(20,10,26,0.75);' +
      'border:1px solid #ffcf5a;border-radius:8px;cursor:pointer;'
    reloadBtn.addEventListener('click', () => location.reload())
    document.body.appendChild(reloadBtn)
  }

  logger.info(
    `啟動完成，目前方向：${orientationOf(app.screen.width, app.screen.height)}，狀態：${String(controller.currentState)}`,
  )
}

bootstrap().catch((error: unknown) => {
  logger.error('啟動失敗', error)
})
